import {useState} from 'react';
import {AlertCircle, RefreshCw} from 'lucide-react';

import {v2Api} from '@/lib/api';
import {Button} from '@/components/ui/button';
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert';
import {cn} from '@/lib/utils';

export type SelectedArchiveItem = { uuid: string; scenario_id: string };

/** Points earned for an archived participant flag (API may omit obtained_score on older archives). */
function resolveArchiveFlagObtainedScore(flag: {
    obtained_score?: number | null;
    score?: number | null;
    hint_penalty?: number | null;
    is_solved?: boolean;
    is_hint_used?: boolean;
    submitted_responses?: Array<{ is_correct?: boolean; obtained_score?: number | null }>;
}): number {
    const maxScore = Number(flag.score ?? 0) || 0;
    const direct = Number(flag.obtained_score ?? NaN);
    if (Number.isFinite(direct) && direct > 0) return direct;

    const responses = Array.isArray(flag.submitted_responses) ? flag.submitted_responses : [];
    const fromResponses = responses.reduce(
        (sum, r) => sum + Math.max(0, Number(r?.obtained_score ?? 0) || 0),
        0,
    );
    if (fromResponses > 0) return fromResponses;

    const anyCorrect = responses.some((r) => Boolean(r?.is_correct));
    const solved = Boolean(flag.is_solved) || anyCorrect;
    if (!solved || maxScore <= 0) return 0;

    const hintPenalty = flag.is_hint_used ? Math.max(0, Number(flag.hint_penalty ?? 0) || 0) : 0;
    return Math.max(0, maxScore - hintPenalty);
}

type FeedbackQuestionItem = { id?: string; question?: string; answer?: unknown };

function normalizeFeedbackPayload(payload: Record<string, unknown>): Record<string, unknown> {
    const nested = payload.feedback;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        return nested as Record<string, unknown>;
    }
    return payload;
}

function feedbackQuestionItems(payload: Record<string, unknown>, sectionKey: string): FeedbackQuestionItem[] {
    const section = payload[sectionKey];
    return Array.isArray(section) ? (section as FeedbackQuestionItem[]) : [];
}

function feedbackAnswerById(items: FeedbackQuestionItem[], id: string): unknown {
    const target = id.toLowerCase();
    const hit = items.find((item) => String(item?.id ?? '').trim().toLowerCase() === target);
    return hit?.answer;
}

interface BulkArchiveReportExportProps {
    selectedArchives: SelectedArchiveItem[];
    onClearSelection: () => void;
    onBusyChange?: (busy: boolean) => void;
    onErrorChange?: (error: string | null) => void;
    showMessages?: boolean;
}

export default function BulkArchiveReportExport(props: BulkArchiveReportExportProps) {
    const {selectedArchives, onClearSelection, onBusyChange, onErrorChange, showMessages = true} = props;
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkUiError, setBulkUiError] = useState<string | null>(null);

    const selectedArchiveUuids = selectedArchives.map((a) => a.uuid);
    const selectedCount = selectedArchiveUuids.length;
    const selectedScenarioIds = Array.from(
        new Set(selectedArchives.map((a) => (a.scenario_id || '').trim()).filter(Boolean)),
    );
    const hasScenarioMismatch = selectedScenarioIds.length > 1;
    const hasSelection = selectedCount > 0;

    const setBusy = (busy: boolean) => {
        setBulkLoading(busy);
        onBusyChange?.(busy);
    };
    const setUiError = (msg: string | null) => {
        setBulkUiError(msg);
        onErrorChange?.(msg);
    };
    const formatDateTime = (value?: string | null) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);
        const yy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    };

    const downloadSelectedReports = async () => {
        if (selectedArchiveUuids.length === 0 || bulkLoading) return;
        setUiError(null);
        if (hasScenarioMismatch) {
            setUiError('Please select archives from the same scenario only.');
            return;
        }
        setBusy(true);
        try {
            const res = await v2Api.dashboard.exerciseArchives.bulkReports(selectedArchiveUuids);
            const existingByUuid = new Map(res.results.map((r) => [r.archive_uuid, r] as const));
            const missingFromResponse = selectedArchiveUuids.filter((id) => !existingByUuid.has(id));
            if (missingFromResponse.length > 0) {
                const recovered = await Promise.all(
                    missingFromResponse.map(async (archiveUuid) => {
                        const report = await v2Api.dashboard.exerciseArchives.immutableReport(archiveUuid);
                        return {archive_uuid: archiveUuid, report};
                    }),
                );
                for (const item of recovered) {
                    existingByUuid.set(item.archive_uuid, item);
                }
                res.results = selectedArchiveUuids
                    .map((id) => existingByUuid.get(id))
                    .filter((x): x is { archive_uuid: string; report: any } => Boolean(x));
            }
            const {Workbook} = await import('exceljs');
            const workbook = new Workbook();

            const normalizePhase = (name?: string): string => {
                const n = String(name || '').trim();
                return n || 'Unassigned';
            };
            const phaseOrder = (all: string[], preferred: string[]): string[] => {
                const uniq = Array.from(new Set(all.map(normalizePhase)));
                const preferredNorm = preferred.map(normalizePhase);
                const known = preferredNorm.filter((p) => uniq.includes(p));
                const extra = uniq.filter((p) => !preferredNorm.includes(p)).sort((a, b) => a.localeCompare(b));
                return [...known, ...extra];
            };
            /** Sum numeric cells in AOA rows (Excel 1-based start/end inclusive). Avoid Excel formulas so totals show without recalculation. */
            const sumAoaCol = (
                rows: Array<Array<string | number | null | { formula: string }>>,
                excelStartRow: number,
                excelEndRow: number,
                colIdx: number,
            ): number => {
                let sum = 0;
                for (let r = excelStartRow - 1; r < excelEndRow; r += 1) {
                    const v = rows[r]?.[colIdx];
                    if (typeof v === 'number' && Number.isFinite(v)) sum += v;
                }
                return sum;
            };
            const countAoaYes = (
                rows: Array<Array<string | number | null | { formula: string }>>,
                excelStartRow: number,
                excelEndRow: number,
                colIdx: number,
            ): number => {
                let count = 0;
                for (let r = excelStartRow - 1; r < excelEndRow; r += 1) {
                    if (String(rows[r]?.[colIdx] ?? '') === 'Yes') count += 1;
                }
                return count;
            };

            const orderedReports = selectedArchiveUuids
                .map((id) => res.results.find((r) => r.archive_uuid === id))
                .filter((x): x is (typeof res.results)[number] => Boolean(x));
            if (orderedReports.length === 0) {
                throw new Error('No reports found for selected archives.');
            }

            const firstScenarioPhases = Array.isArray((orderedReports[0].report as any)?.scenario?.phases)
                ? (((orderedReports[0].report as any).scenario.phases as Array<{ phase_name?: string; order?: number }>)
                    .slice()
                    .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
                    .map((p) => String(p?.phase_name || '').trim())
                    .filter(Boolean))
                : [];

            const allParticipants = orderedReports.flatMap((r) => r.report.participants ?? []);
            const objectivePhases = phaseOrder(
                allParticipants.flatMap((p) => (p.score?.scenarios?.phase ?? []).map((ph) => ph?.name || '')),
                firstScenarioPhases,
            );
            const subjectivePhases = phaseOrder(
                allParticipants.flatMap((p) => (p.score?.ttx?.phase ?? []).map((ph) => ph?.name || '')),
                firstScenarioPhases,
            );
            const objective = objectivePhases.length ? objectivePhases : ['Unassigned'];
            const subjective = subjectivePhases.length ? subjectivePhases : ['Unassigned'];
            const combinedMerged = phaseOrder(
                [...objectivePhases, ...subjectivePhases],
                firstScenarioPhases,
            );
            const combined = combinedMerged.length ? combinedMerged : ['Unassigned'];

            const header1: Array<string | null> = ['Report', 'Participant', null, null, 'Objective'];
            for (let i = 1; i < objective.length * 2; i += 1) header1.push(null);
            header1.push('Subjective');
            for (let i = 1; i < subjective.length * 2; i += 1) header1.push(null);
            header1.push('Objective + Subjective');
            for (let i = 1; i < combined.length * 2; i += 1) header1.push(null);
            header1.push('Grand Total', null, null);

            const header2: Array<string | null> = [null, null, null, null];
            for (const ph of objective) header2.push(ph, null);
            for (const ph of subjective) header2.push(ph, null);
            for (const ph of combined) header2.push(ph, null);
            header2.push(null, null, null);

            const header3: Array<string | null> = ['Report Name', 'ID', 'Name', 'Team'];
            for (let i = 0; i < objective.length + subjective.length + combined.length; i += 1) {
                header3.push('Obtained', 'Out of');
            }
            header3.push('Obtained', 'Out of', 'Per.%');

            const aoa: Array<Array<string | number | null | { formula: string }>> = [header1, header2, header3];
            const totalRowIndices: number[] = [];
            const archiveNameMergeRanges: Array<{ startRow: number; endRow: number }> = [];
            for (const reportItem of orderedReports) {
                const archiveStartRow = aoa.length + 1;
                const participants = reportItem.report.participants ?? [];
                for (let pIdx = 0; pIdx < participants.length; pIdx += 1) {
                    const p = participants[pIdx];
                    const scenarioPhaseMap = new Map<string, { obtained: number; outOf: number }>();
                    const ttxPhaseMap = new Map<string, { obtained: number; outOf: number }>();
                    for (const ph of p.score?.scenarios?.phase ?? []) {
                        const name = normalizePhase(ph?.name);
                        scenarioPhaseMap.set(name, {
                            obtained: Number(ph?.obtained_score ?? 0) + Number(ph?.bonus_points ?? 0),
                            outOf: Number(ph?.out_of ?? 0),
                        });
                    }
                    for (const ph of p.score?.ttx?.phase ?? []) {
                        const name = normalizePhase(ph?.name);
                        ttxPhaseMap.set(name, {
                            obtained: Number(ph?.obtained_score ?? 0) + Number(ph?.bonus_points ?? 0),
                            outOf: Number(ph?.out_of ?? 0),
                        });
                    }
                    const row: Array<string | number | null | { formula: string }> = [
                        pIdx === 0 ? (reportItem.report.name || reportItem.archive_uuid) : null,
                        p.user_id,
                        p.full_name || `User ${p.user_id}`,
                        p.team?.name || '—',
                    ];
                    for (const ph of objective) {
                        const val = scenarioPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                        row.push(val.obtained, val.outOf);
                    }
                    for (const ph of subjective) {
                        const val = ttxPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                        row.push(val.obtained, val.outOf);
                    }
                    for (const ph of combined) {
                        const sc = scenarioPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                        const ttx = ttxPhaseMap.get(ph) || {obtained: 0, outOf: 0};
                        row.push(sc.obtained + ttx.obtained, sc.outOf + ttx.outOf);
                    }
                    const objectiveTotalObtained = objective.reduce((acc, ph) => acc + (scenarioPhaseMap.get(ph)?.obtained || 0), 0);
                    const objectiveTotalOutOf = objective.reduce((acc, ph) => acc + (scenarioPhaseMap.get(ph)?.outOf || 0), 0);
                    const subjectiveTotalObtained = subjective.reduce((acc, ph) => acc + (ttxPhaseMap.get(ph)?.obtained || 0), 0);
                    const subjectiveTotalOutOf = subjective.reduce((acc, ph) => acc + (ttxPhaseMap.get(ph)?.outOf || 0), 0);
                    const grandObtained = objectiveTotalObtained + subjectiveTotalObtained;
                    const grandOutOf = objectiveTotalOutOf + subjectiveTotalOutOf;
                    row.push(grandObtained, grandOutOf, grandOutOf > 0 ? Number(((grandObtained / grandOutOf) * 100).toFixed(2)) : 0);
                    aoa.push(row);
                }
                const archiveEndRow = aoa.length;
                if (participants.length > 0) {
                    archiveNameMergeRanges.push({startRow: archiveStartRow, endRow: archiveEndRow});
                }
                const numericCols = (objective.length + subjective.length + combined.length) * 2;
                const totalRow: Array<string | number | null> = [
                    null,
                    null,
                    'Total',
                    null,
                ];
                for (let i = 0; i < numericCols; i += 1) {
                    const colIdx = 4 + i;
                    totalRow.push(sumAoaCol(aoa, archiveStartRow, archiveEndRow, colIdx));
                }
                const grandObtainedSum = sumAoaCol(aoa, archiveStartRow, archiveEndRow, 4 + numericCols);
                const grandOutOfSum = sumAoaCol(aoa, archiveStartRow, archiveEndRow, 5 + numericCols);
                const totalRowIndex = aoa.length + 1;
                totalRow.push(
                    grandObtainedSum,
                    grandOutOfSum,
                    grandOutOfSum > 0 ? Number(((grandObtainedSum / grandOutOfSum) * 100).toFixed(2)) : 0,
                );
                aoa.push(totalRow);
                totalRowIndices.push(totalRowIndex);
                // Visual separation between archive blocks in the combined sheet.
                aoa.push(Array.from({length: 7 + numericCols}, () => null));
            }

            const ws = workbook.addWorksheet('Overall');
            aoa.forEach((row, rIdx) => {
                row.forEach((val, cIdx) => {
                    ws.getCell(rIdx + 1, cIdx + 1).value = val as any;
                });
            });

            const objectiveStart = 5;
            const objectiveEnd = objectiveStart + objective.length * 2 - 1;
            const subjectiveStart = objectiveEnd + 1;
            const subjectiveEnd = subjectiveStart + subjective.length * 2 - 1;
            const combinedStart = subjectiveEnd + 1;
            const combinedEnd = combinedStart + combined.length * 2 - 1;
            const grandStart = combinedEnd + 1;
            const grandEnd = grandStart + 2;
            const numericCols = (objective.length + subjective.length + combined.length) * 2;

            ws.mergeCells(1, 1, 2, 1);
            ws.mergeCells(1, 2, 2, 4);
            ws.mergeCells(1, objectiveStart, 1, objectiveEnd);
            ws.mergeCells(1, subjectiveStart, 1, subjectiveEnd);
            ws.mergeCells(1, combinedStart, 1, combinedEnd);
            ws.mergeCells(1, grandStart, 2, grandEnd);
            objective.forEach((_, i) => ws.mergeCells(2, objectiveStart + i * 2, 2, objectiveStart + i * 2 + 1));
            subjective.forEach((_, i) => ws.mergeCells(2, subjectiveStart + i * 2, 2, subjectiveStart + i * 2 + 1));
            combined.forEach((_, i) => ws.mergeCells(2, combinedStart + i * 2, 2, combinedStart + i * 2 + 1));

            ws.columns = [
                {width: 28},
                {width: 14},
                {width: 28},
                {width: 18},
                ...Array.from({length: numericCols}, () => ({width: 11})),
                {width: 11},
                {width: 11},
                {width: 11},
            ];

            const styleRange = (rowIndex: number, colStart: number, colEnd: number, bgArgb: string) => {
                for (let c = colStart; c <= colEnd; c += 1) {
                    const cell = ws.getCell(rowIndex, c);
                    cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: bgArgb}};
                    cell.font = {bold: true, color: {argb: 'FF111827'}};
                    cell.alignment = {horizontal: 'center', vertical: 'middle'};
                    cell.border = {
                        top: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        left: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        bottom: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        right: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    };
                }
            };

            styleRange(1, 1, 4, 'FFE5E7EB');
            styleRange(2, 1, 4, 'FFF3F4F6');
            styleRange(3, 1, 4, 'FFF9FAFB');
            styleRange(1, objectiveStart, objectiveEnd, 'FFDDEBF7');
            styleRange(2, objectiveStart, objectiveEnd, 'FFEAF3FB');
            styleRange(3, objectiveStart, objectiveEnd, 'FFF2F8FD');
            styleRange(1, subjectiveStart, subjectiveEnd, 'FFFCE4D6');
            styleRange(2, subjectiveStart, subjectiveEnd, 'FFFDF0E8');
            styleRange(3, subjectiveStart, subjectiveEnd, 'FFFEF6EF');
            styleRange(1, combinedStart, combinedEnd, 'FFE2F0D9');
            styleRange(2, combinedStart, combinedEnd, 'FFECF6E8');
            styleRange(3, combinedStart, combinedEnd, 'FFF3FAF0');
            styleRange(1, grandStart, grandEnd, 'FFFFF2CC');
            styleRange(2, grandStart, grandEnd, 'FFFFF8DE');
            styleRange(3, grandStart, grandEnd, 'FFFFFBEB');
            for (const rowIndex of totalRowIndices) {
                styleRange(rowIndex, 1, grandEnd, 'FFFFF2CC');
                const labelCell = ws.getCell(rowIndex, 3);
                labelCell.alignment = {horizontal: 'left', vertical: 'middle'};
            }
            for (const range of archiveNameMergeRanges) {
                if (range.endRow > range.startRow) {
                    ws.mergeCells(range.startRow, 1, range.endRow, 1);
                }
                const archiveCell = ws.getCell(range.startRow, 1);
                archiveCell.alignment = {horizontal: 'left', vertical: 'middle'};
            }

            // Flags sheet (bulk, grouped by archive with per-archive totals).
            const flagsHeader1: Array<string | null> = [
                'Report',
                'Participant',
                null,
                'Flags',
                null,
                null,
                null,
                'Result',
                null,
                null,
                'Status',
                null,
                null,
            ];
            const flagsHeader2: Array<string | null> = [
                null,
                'Name',
                'Team',
                'Flag ID',
                'Phase',
                'Question',
                'Attempts',
                'Obtained',
                'Out of',
                'Per.%',
                'Solved',
                'Hint Used',
                'Submitted Responses',
            ];
            const flagsAoa: Array<Array<string | number | null | {formula: string}>> = [flagsHeader1, flagsHeader2];
            const flagsTotalRows: number[] = [];
            const flagsArchiveMergeRanges: Array<{startRow: number; endRow: number}> = [];

            for (const reportItem of orderedReports) {
                const archiveRowsStart = flagsAoa.length + 1;
                const participants = reportItem.report.participants ?? [];
                for (const p of participants) {
                    const flags = Array.isArray((p as any).flags) ? ((p as any).flags as any[]) : [];
                    if (flags.length === 0) {
                        flagsAoa.push([
                            reportItem.report.name || reportItem.archive_uuid,
                            p.full_name || `User ${p.user_id}`,
                            p.team?.name || '—',
                            '',
                            '',
                            '',
                            0,
                            0,
                            0,
                            0,
                            'No',
                            'No',
                            '',
                        ]);
                        continue;
                    }
                    for (let i = 0; i < flags.length; i += 1) {
                        const f = flags[i];
                        const responses = Array.isArray(f?.submitted_responses) ? f.submitted_responses : [];
                        const attempts = responses.length;
                        const obtained = resolveArchiveFlagObtainedScore(f);
                        const outOf = Number(f?.score ?? 0) || 0;
                        const per = outOf > 0 ? Number(((obtained / outOf) * 100).toFixed(2)) : 0;
                        const submittedResponses = responses
                            .map((r: any) => String(r?.submitted_response ?? '').trim())
                            .filter(Boolean)
                            .join(' | ');
                        flagsAoa.push([
                            i === 0 ? (reportItem.report.name || reportItem.archive_uuid) : null,
                            p.full_name || `User ${p.user_id}`,
                            p.team?.name || '—',
                            String(f?.flag_id ?? ''),
                            String(f?.phase_name ?? ''),
                            String(f?.question ?? ''),
                            attempts,
                            obtained,
                            outOf,
                            per,
                            f?.is_solved ? 'Yes' : 'No',
                            f?.is_hint_used ? 'Yes' : 'No',
                            submittedResponses,
                        ]);
                    }
                }
                const archiveRowsEnd = flagsAoa.length;
                if (archiveRowsEnd >= archiveRowsStart) {
                    flagsArchiveMergeRanges.push({startRow: archiveRowsStart, endRow: archiveRowsEnd});
                }

                const totalRowIndex = flagsAoa.length + 1;
                const start = archiveRowsStart;
                const end = archiveRowsEnd;
                const attemptsSum = sumAoaCol(flagsAoa, start, end, 6);
                const obtainedSum = sumAoaCol(flagsAoa, start, end, 7);
                const outOfSum = sumAoaCol(flagsAoa, start, end, 8);
                flagsAoa.push([
                    null,
                    `Total (${reportItem.report.name || reportItem.archive_uuid})`,
                    '',
                    '',
                    '',
                    '',
                    attemptsSum,
                    obtainedSum,
                    outOfSum,
                    outOfSum > 0 ? Number(((obtainedSum / outOfSum) * 100).toFixed(2)) : 0,
                    countAoaYes(flagsAoa, start, end, 10),
                    countAoaYes(flagsAoa, start, end, 11),
                    '',
                ]);
                flagsTotalRows.push(totalRowIndex);
                flagsAoa.push(Array.from({length: 13}, () => null));
            }

            const wsFlags = workbook.addWorksheet('Flags');
            flagsAoa.forEach((row, rIdx) => {
                row.forEach((val, cIdx) => {
                    wsFlags.getCell(rIdx + 1, cIdx + 1).value = val as any;
                });
            });

            wsFlags.mergeCells(1, 1, 2, 1);
            wsFlags.mergeCells(1, 2, 1, 3);
            wsFlags.mergeCells(1, 4, 1, 7);
            wsFlags.mergeCells(1, 8, 1, 10);
            wsFlags.mergeCells(1, 11, 1, 13);

            wsFlags.columns = [
                {width: 28},
                {width: 26},
                {width: 18},
                {width: 16},
                {width: 14},
                {width: 36},
                {width: 10},
                {width: 10},
                {width: 10},
                {width: 10},
                {width: 10},
                {width: 12},
                {width: 52},
            ];

            const styleFlagsRange = (rowIndex: number, colStart: number, colEnd: number, bgArgb: string) => {
                for (let c = colStart; c <= colEnd; c += 1) {
                    const cell = wsFlags.getCell(rowIndex, c);
                    cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: bgArgb}};
                    cell.font = {bold: true, color: {argb: 'FF111827'}};
                    cell.alignment = {horizontal: 'center', vertical: 'middle', wrapText: true};
                    cell.border = {
                        top: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        left: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        bottom: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        right: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    };
                }
            };
            styleFlagsRange(1, 1, 1, 'FFE5E7EB');
            styleFlagsRange(2, 1, 1, 'FFF3F4F6');
            styleFlagsRange(1, 2, 3, 'FFE5E7EB');
            styleFlagsRange(2, 2, 3, 'FFF3F4F6');
            styleFlagsRange(1, 4, 7, 'FFDDEBF7');
            styleFlagsRange(2, 4, 7, 'FFEAF3FB');
            styleFlagsRange(1, 8, 10, 'FFE2F0D9');
            styleFlagsRange(2, 8, 10, 'FFECF6E8');
            styleFlagsRange(1, 11, 13, 'FFFCE4D6');
            styleFlagsRange(2, 11, 13, 'FFFDF0E8');

            for (const rowIndex of flagsTotalRows) {
                styleFlagsRange(rowIndex, 1, 13, 'FFFFF2CC');
                wsFlags.getCell(rowIndex, 2).alignment = {horizontal: 'left', vertical: 'middle'};
            }
            for (const range of flagsArchiveMergeRanges) {
                if (range.endRow > range.startRow) {
                    wsFlags.mergeCells(range.startRow, 1, range.endRow, 1);
                }
                wsFlags.getCell(range.startRow, 1).alignment = {horizontal: 'left', vertical: 'middle'};
            }

            // Injects sheet (bulk, grouped by archive with per-archive totals).
            const injectsHeader1: Array<string | null> = [
                'Report',
                'Participant',
                null,
                'Inject',
                null,
                null,
                null,
                'Timeline',
                null,
                'Scoring',
                null,
                null,
                null,
                null,
                'Status',
                null,
            ];
            const injectsHeader2: Array<string | null> = [
                null,
                'Name',
                'Team',
                'Inject ID',
                'Title',
                'Points',
                'Points Type',
                'Sent At',
                'Responded At',
                'Obtained',
                'Bonus',
                'Total',
                'Out of',
                'Per.%',
                'Correct',
                'Hint Used',
            ];

            const injectsAoa: Array<Array<string | number | null | {formula: string}>> = [injectsHeader1, injectsHeader2];
            const injectsTotalRows: number[] = [];
            const injectArchiveMergeRanges: Array<{startRow: number; endRow: number}> = [];

            const participantKey = (userId?: number | null, teamId?: number | null) =>
                `${Number(userId ?? 0)}:${Number(teamId ?? 0)}`;

            for (const reportItem of orderedReports) {
                const participantByKey = new Map<string, {name: string; team: string}>();
                for (const p of reportItem.report.participants ?? []) {
                    participantByKey.set(
                        participantKey(p.user_id, p.team?.id ?? 0),
                        {
                            name: p.full_name || `User ${p.user_id}`,
                            team: p.team?.name || '—',
                        },
                    );
                }

                const archiveRowsStart = injectsAoa.length + 1;
                const deliveries = reportItem.report.inject_deliveries ?? [];
                if (deliveries.length === 0) {
                    injectsAoa.push([
                        reportItem.report.name || reportItem.archive_uuid,
                        '',
                        '',
                        '',
                        '',
                        0,
                        '',
                        '',
                        '',
                        0,
                        0,
                        0,
                        0,
                        0,
                        '',
                        '',
                    ]);
                } else {
                    for (let i = 0; i < deliveries.length; i += 1) {
                        const d = deliveries[i];
                        const who = participantByKey.get(participantKey(d.user_id, d.role_id)) ?? {
                            name: `User ${d.user_id}`,
                            team: '—',
                        };
                        const obtained = Number(d.obtained_score ?? 0) || 0;
                        const bonus = Number(d.bonus_points ?? 0) || 0;
                        const total = obtained + bonus;
                        const outOf = Number(d.inject_points ?? 0) || 0;
                        const per = outOf > 0 ? Number(((total / outOf) * 100).toFixed(2)) : 0;
                        injectsAoa.push([
                            i === 0 ? (reportItem.report.name || reportItem.archive_uuid) : null,
                            who.name,
                            who.team,
                            d.inject_id,
                            d.inject_title || '',
                            outOf,
                            d.inject_points_type || '',
                            d.sent_at || '',
                            d.responded_at || '',
                            obtained,
                            bonus,
                            total,
                            outOf,
                            per,
                            d.is_correct ? 'Yes' : 'No',
                            d.hint_used ? 'Yes' : 'No',
                        ]);
                    }
                }

                const archiveRowsEnd = injectsAoa.length;
                if (archiveRowsEnd >= archiveRowsStart) {
                    injectArchiveMergeRanges.push({startRow: archiveRowsStart, endRow: archiveRowsEnd});
                }

                const totalRowIndex = injectsAoa.length + 1;
                const start = archiveRowsStart;
                const end = archiveRowsEnd;
                const pointsOutOfSum = sumAoaCol(injectsAoa, start, end, 5);
                const obtainedSum = sumAoaCol(injectsAoa, start, end, 9);
                const bonusSum = sumAoaCol(injectsAoa, start, end, 10);
                const totalSum = sumAoaCol(injectsAoa, start, end, 11);
                const outOfSum = sumAoaCol(injectsAoa, start, end, 12);
                injectsAoa.push([
                    null,
                    `Total (${reportItem.report.name || reportItem.archive_uuid})`,
                    '',
                    '',
                    '',
                    pointsOutOfSum,
                    '',
                    '',
                    '',
                    obtainedSum,
                    bonusSum,
                    totalSum,
                    outOfSum,
                    outOfSum > 0 ? Number(((totalSum / outOfSum) * 100).toFixed(2)) : 0,
                    countAoaYes(injectsAoa, start, end, 14),
                    countAoaYes(injectsAoa, start, end, 15),
                ]);
                injectsTotalRows.push(totalRowIndex);
                injectsAoa.push(Array.from({length: 16}, () => null));
            }

            const wsInjects = workbook.addWorksheet('Injects');
            injectsAoa.forEach((row, rIdx) => {
                row.forEach((val, cIdx) => {
                    wsInjects.getCell(rIdx + 1, cIdx + 1).value = val as any;
                });
            });

            wsInjects.mergeCells(1, 1, 2, 1);
            wsInjects.mergeCells(1, 2, 1, 3);
            wsInjects.mergeCells(1, 4, 1, 7);
            wsInjects.mergeCells(1, 8, 1, 9);
            wsInjects.mergeCells(1, 10, 1, 14);
            wsInjects.mergeCells(1, 15, 1, 16);

            wsInjects.columns = [
                {width: 28},
                {width: 24},
                {width: 16},
                {width: 10},
                {width: 34},
                {width: 10},
                {width: 12},
                {width: 20},
                {width: 20},
                {width: 10},
                {width: 10},
                {width: 10},
                {width: 10},
                {width: 10},
                {width: 10},
                {width: 10},
            ];

            const styleInjectRange = (rowIndex: number, colStart: number, colEnd: number, bgArgb: string) => {
                for (let c = colStart; c <= colEnd; c += 1) {
                    const cell = wsInjects.getCell(rowIndex, c);
                    cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: bgArgb}};
                    cell.font = {bold: true, color: {argb: 'FF111827'}};
                    cell.alignment = {horizontal: 'center', vertical: 'middle', wrapText: true};
                    cell.border = {
                        top: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        left: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        bottom: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                        right: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    };
                }
            };
            styleInjectRange(1, 1, 1, 'FFE5E7EB');
            styleInjectRange(2, 1, 1, 'FFF3F4F6');
            styleInjectRange(1, 2, 3, 'FFE5E7EB');
            styleInjectRange(2, 2, 3, 'FFF3F4F6');
            styleInjectRange(1, 4, 7, 'FFDDEBF7');
            styleInjectRange(2, 4, 7, 'FFEAF3FB');
            styleInjectRange(1, 8, 9, 'FFE2F0D9');
            styleInjectRange(2, 8, 9, 'FFECF6E8');
            styleInjectRange(1, 10, 14, 'FFFCE4D6');
            styleInjectRange(2, 10, 14, 'FFFDF0E8');
            styleInjectRange(1, 15, 16, 'FFE8DEF8');
            styleInjectRange(2, 15, 16, 'FFF1EBFC');

            for (const rowIndex of injectsTotalRows) {
                styleInjectRange(rowIndex, 1, 16, 'FFFFF2CC');
                wsInjects.getCell(rowIndex, 2).alignment = {horizontal: 'left', vertical: 'middle'};
            }
            for (const range of injectArchiveMergeRanges) {
                if (range.endRow > range.startRow) {
                    wsInjects.mergeCells(range.startRow, 1, range.endRow, 1);
                }
                wsInjects.getCell(range.startRow, 1).alignment = {horizontal: 'left', vertical: 'middle'};
            }

            // Feedback sheet (sample-style format: All Responses + Summary).
            type FeedbackTemplateRow = {
                sl: number;
                entityCode: string;
                userId: string | number;
                scenario: string;
                exerciseDate: string;
                submittedAt: string;
                q1: number | '';
                q2: number | '';
                q3: number | '';
                q4: number | '';
                q5: number | '';
                q6: number | '';
                q7: number | '';
                avg: number | '';
                gap1: string;
                gap2: string;
                gap3: string;
            };
            const flattenPayloadMap = (payload: Record<string, unknown>) => {
                const out = new Map<string, unknown>();
                const walk = (value: unknown, path: string) => {
                    if (Array.isArray(value)) {
                        value.forEach((v, i) => walk(v, `${path}[${i}]`));
                        if (value.length === 0 && path) out.set(path.toLowerCase(), value);
                        return;
                    }
                    if (value && typeof value === 'object') {
                        const obj = value as Record<string, unknown>;
                        const keys = Object.keys(obj);
                        if (keys.length === 0 && path) out.set(path.toLowerCase(), obj);
                        keys.forEach((k) => walk(obj[k], path ? `${path}.${k}` : k));
                        return;
                    }
                    if (path) out.set(path.toLowerCase(), value);
                };
                walk(payload, '');
                return out;
            };
            const findByHints = (m: Map<string, unknown>, hints: string[]) => {
                for (const [k, v] of Array.from(m.entries())) {
                    const lk = k.toLowerCase();
                    if (hints.every((h) => lk.includes(h.toLowerCase()))) return v;
                }
                return undefined;
            };
            const toNum = (v: unknown): number | '' => {
                const n = Number(v);
                return Number.isFinite(n) ? n : '';
            };
            const toStr = (v: unknown): string => {
                if (v == null) return '';
                if (typeof v === 'string') return v;
                if (typeof v === 'number' || typeof v === 'boolean') return String(v);
                try {
                    return JSON.stringify(v);
                } catch {
                    return String(v);
                }
            };
            const rangeText = (start?: string | null, end?: string | null): string => {
                if (!start && !end) return '';
                const formatDateOnly = (raw?: string | null) => {
                    if (!raw) return '';
                    const d = new Date(raw);
                    if (Number.isNaN(d.getTime())) return String(raw);
                    return d.toLocaleDateString('en-GB', {day: '2-digit', month: 'long', year: 'numeric'});
                };
                const s = formatDateOnly(start);
                const e = formatDateOnly(end);
                if (s && e && s !== e) return `${s} - ${e}`;
                return s || e;
            };

            const feedbackRows: FeedbackTemplateRow[] = [];
            let sl = 1;
            for (const reportItem of orderedReports) {
                const scenarioLabel = reportItem.report.name || reportItem.archive_uuid;
                const exerciseDate = rangeText(reportItem.report.start_at ?? '', reportItem.report.end_at ?? '');
                const items = Array.isArray(reportItem.report.feedback) ? reportItem.report.feedback : [];
                for (const item of items) {
                    const rawPayload = (item.payload && typeof item.payload === 'object'
                        ? (item.payload as Record<string, unknown>)
                        : {}) as Record<string, unknown>;
                    const payload = normalizeFeedbackPayload(rawPayload);
                    const m = flattenPayloadMap(payload);
                    const questionItems = feedbackQuestionItems(payload, 'questions');
                    const gapItems = feedbackQuestionItems(payload, 'gaps');
                    const sectionA =
                        payload.section_a && typeof payload.section_a === 'object'
                            ? (payload.section_a as Record<string, unknown>)
                            : null;
                    const sectionB =
                        payload.section_b && typeof payload.section_b === 'object'
                            ? (payload.section_b as Record<string, unknown>)
                            : null;
                    const readRating = (id: string): number | '' => {
                        const fromQuestions = feedbackAnswerById(questionItems, id);
                        if (fromQuestions != null && fromQuestions !== '') {
                            const n = toNum(fromQuestions);
                            if (n !== '') return n;
                        }
                        const fromSectionA = sectionA?.[id];
                        if (fromSectionA != null && fromSectionA !== '') {
                            const n = toNum(fromSectionA);
                            if (n !== '') return n;
                        }
                        return toNum(findByHints(m, [id]) ?? findByHints(m, [`question${id.slice(1)}`]));
                    };
                    const readGap = (n: 1 | 2 | 3): string => {
                        const id = `gap${n}`;
                        const fromGaps = feedbackAnswerById(gapItems, id);
                        if (fromGaps != null && fromGaps !== '') return toStr(fromGaps);
                        const fromSectionB = sectionB?.[id];
                        if (fromSectionB != null && fromSectionB !== '') return toStr(fromSectionB);
                        return toStr(findByHints(m, ['gap', String(n)]) ?? findByHints(m, ['q8', String(n)]));
                    };
                    const entityCode =
                        toStr(findByHints(m, ['entity', 'code'])) ||
                        toStr(findByHints(m, ['bank', 'code'])) ||
                        toStr(item.submitted_by_name ?? '');
                    const userId = toStr(item.participant_user_id ?? item.submitted_by_id ?? '');
                    const q1 = readRating('q1');
                    const q2 = readRating('q2');
                    const q3 = readRating('q3');
                    const q4 = readRating('q4');
                    const q5 = readRating('q5');
                    const q6 = readRating('q6');
                    const q7 = readRating('q7');
                    const numeric = [q1, q2, q3, q4, q5, q6, q7].filter((n): n is number => typeof n === 'number');
                    const avg = numeric.length ? Number((numeric.reduce((a, b) => a + b, 0) / numeric.length).toFixed(2)) : '';
                    const gap1 = readGap(1);
                    const gap2 = readGap(2);
                    const gap3 = readGap(3);

                    feedbackRows.push({
                        sl: sl++,
                        entityCode,
                        userId,
                        scenario: scenarioLabel,
                        exerciseDate,
                        submittedAt: formatDateTime(item.created_at ?? ''),
                        q1, q2, q3, q4, q5, q6, q7,
                        avg,
                        gap1,
                        gap2,
                        gap3,
                    });
                }
            }

            const wsFeedbackAll = workbook.addWorksheet('Feedback');
            const feedbackHeaders = [
                'Sl.',
                'Entity Code',
                'User ID',
                'Scenario',
                'Exercise Date',
                'Submitted At',
                'Q1\nThe cyber exercise scenarios were realistic and relevant to real-world cyber threats faced by banks.',
                "Q2\nThe exercise effectively tested your bank's preparedness and decision-making under pressure.",
                'Q3\nThe sequence and timing of injects were appropriate and well-coordinated throughout the drill.',
                'Q4\nThe exercise resulted in clear learning outcomes for participating teams.',
                'Q5\nThe Cyber Range platform was stable and accessible throughout the exercise.',
                'Q6\nThe platform interface and navigation were intuitive and easy to use.',
                'Q7\nOverall experience of the Cyber Exercise.',
                'Average\n(Q1–Q7)',
                "Q8 — Gap 1\nGaps in your bank's existing SOPs identified during the exercise.",
                "Q8 — Gap 2\nGaps in your bank's existing SOPs identified during the exercise.",
                "Q8 — Gap 3\nGaps in your bank's existing SOPs identified during the exercise.",
            ];
            wsFeedbackAll.addRow(feedbackHeaders);
            feedbackRows.forEach((r) => {
                wsFeedbackAll.addRow([
                    r.sl,
                    r.entityCode,
                    r.userId,
                    r.scenario,
                    r.exerciseDate,
                    r.submittedAt,
                    r.q1, r.q2, r.q3, r.q4, r.q5, r.q6, r.q7,
                    r.avg,
                    r.gap1, r.gap2, r.gap3,
                ]);
            });
            wsFeedbackAll.columns = [
                {width: 6}, {width: 16}, {width: 14}, {width: 24}, {width: 20}, {width: 20},
                {width: 18}, {width: 18}, {width: 18}, {width: 18}, {width: 18}, {width: 18}, {width: 18},
                {width: 12}, {width: 26}, {width: 26}, {width: 26},
            ];
            wsFeedbackAll.getRow(1).height = 52;
            wsFeedbackAll.getRow(1).font = {bold: true};
            wsFeedbackAll.getRow(1).alignment = {vertical: 'middle', horizontal: 'center', wrapText: true};
            for (let c = 1; c <= 17; c += 1) {
                const cell = wsFeedbackAll.getCell(1, c);
                cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFE5E7EB'}};
                cell.border = {
                    top: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    left: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    bottom: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                    right: {style: 'thin', color: {argb: 'FFD1D5DB'}},
                };
            }

            const wsFeedbackSummary = workbook.addWorksheet('Feedback summary');
            const qCols = ['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7'];
            const qAverages = qCols.map((_, idx) => {
                const vals = feedbackRows
                    .map((r) => [r.q1, r.q2, r.q3, r.q4, r.q5, r.q6, r.q7][idx])
                    .filter((v): v is number => typeof v === 'number');
                const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
                return Number(avg.toFixed(2));
            });
            wsFeedbackSummary.addRow(['Metric', 'Value']);
            wsFeedbackSummary.addRow(['Total Responses', feedbackRows.length]);
            qCols.forEach((q, idx) => wsFeedbackSummary.addRow([`${q} Average`, qAverages[idx]]));
            const overallAvg = qAverages.length
                ? Number((qAverages.reduce((a, b) => a + b, 0) / qAverages.length).toFixed(2))
                : 0;
            wsFeedbackSummary.addRow(['Overall Average (Q1–Q7)', overallAvg]);
            wsFeedbackSummary.columns = [{width: 30}, {width: 18}];
            wsFeedbackSummary.getRow(1).font = {bold: true};

            if (res.missing.length > 0) {
                const ws = workbook.addWorksheet('Missing');
                ws.getCell(1, 1).value = 'archive_uuid';
                res.missing.forEach((id, idx) => {
                    ws.getCell(idx + 2, 1).value = id;
                });
                ws.getColumn(1).width = 40;
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `selected-archive-reports-${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (e) {
            setUiError(e instanceof Error ? e.message : 'Failed to fetch selected reports');
        } finally {
            setBusy(false);
        }
    };

    if (!hasSelection && !bulkUiError && !bulkLoading) {
        return null;
    }

    return (
        <div className="space-y-3 rounded-lg border border-border/70 bg-muted/20 p-3 dark:bg-muted/10">
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => void downloadSelectedReports()}
                    disabled={selectedArchiveUuids.length === 0 || bulkLoading || hasScenarioMismatch}
                >
                    {bulkLoading ? 'Generating report...' : `Get selected reports (${selectedArchiveUuids.length})`}
                </Button>
                <Button type="button" variant="destructive" size="sm" onClick={onClearSelection} disabled={selectedCount === 0 || bulkLoading}>
                    Clear selection
                </Button>
            </div>

            {showMessages && bulkUiError && (
                <Alert variant="destructive" className="border-destructive/40">
                    <AlertCircle className="h-4 w-4"/>
                    <AlertTitle>Bulk report error</AlertTitle>
                    <AlertDescription>{bulkUiError}</AlertDescription>
                </Alert>
            )}

            {showMessages && selectedCount > 0 && (
                <Alert className={cn('border-border/60', hasScenarioMismatch && 'border-destructive/40')}>
                    <AlertCircle className={cn('h-4 w-4', hasScenarioMismatch ? 'text-destructive' : 'text-primary')}/>
                    <AlertTitle>
                        {hasScenarioMismatch
                            ? 'Mixed scenario selection'
                            : `Selected ${selectedCount} archive${selectedCount === 1 ? '' : 's'}`}
                    </AlertTitle>
                    <AlertDescription>
                        {hasScenarioMismatch
                            ? `You selected ${selectedCount} archives from ${selectedScenarioIds.length} different scenarios.`
                            : `Scenario ID: ${selectedScenarioIds[0] || '—'}. You can now generate the bulk report.`}
                    </AlertDescription>
                    {hasScenarioMismatch && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            Keep one scenario only, then export.
                        </div>
                    )}
                </Alert>
            )}

            {bulkLoading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="rounded-lg border bg-card px-6 py-5 shadow-lg">
                        <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                            <RefreshCw className="h-4 w-4 animate-spin"/>
                            Generating bulk report, please wait...
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

